import React, { useEffect, useState } from 'react';
import { RiArrowRightSLine, RiDashboardLine, RiLayoutRowLine, RiMenuLine, RiTruckLine } from 'react-icons/ri';
import { InformationCard } from '../../general_components/content_management/item_page/informationCard';
import { OverviewCard } from '../../general_components/content_management/item_page/overViewCard';
import { Form } from '../../general_components/forms/Form';
import { MainBoardSettingsPopup } from '../../main_board_components/mainBoardSettingsPopup';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addPopup } from '../../supplementary_components/store';
import { generateColumn } from '../../supplementary_components/itemEditMethods';
import { storeCustomizations } from '../../supplementary_components/userDataDelegate';
import { EMPLOYEE_ADD_CONF } from '../employees/employeeAddConf';
import { ChangePasswordPopup } from './changePasswordPopup';
import { LoadDispatchInfoPopup } from './customizations/loadDispatchInfoPopup';
import { TruckPageLayoutPopup } from './customizations/truckPageLayoutPopup';
import { MenuLayoutPopup } from './customizations/menuLayoutPopup';
import itemPageStyles from '../../general_components/content_management/item_page/itemPage.module.scss';
import styles from './myAccountPage.module.scss';

const SELF_EDITABLE_FIELDS = ['firstName', 'lastName', 'telegramUserName', 'directLine', 'extension'];

const CUSTOMIZATIONS = [
    {
        key: 'menu',
        title: 'Menu',
        description: 'Use the sidebar or the original menu across the top.',
        icon: RiMenuLine,
        popup: MenuLayoutPopup,
    },
    {
        key: 'mainBoard',
        title: 'Main Board',
        description: 'Planner Mode columns, board statistics and timeline scrolling.',
        icon: RiDashboardLine,
        popup: MainBoardSettingsPopup,
    },
    {
        key: 'loadDispatchInfo',
        title: 'Load Dispatch Info',
        description: 'Format of the load info copied from the Stops section to send to drivers.',
        icon: RiTruckLine,
        popup: LoadDispatchInfoPopup,
    },
    {
        key: 'truckPage',
        title: 'Truck Page',
        description: 'Order and visibility of the center column on the truck page.',
        icon: RiLayoutRowLine,
        popup: TruckPageLayoutPopup,
    },
];

const openCustomization = (customization) => {
    addPopup({
        content: customization.popup,
        popupData: {},
        postSuccessAction: () => {},
        EZCloseAllowed: true,
    });
};

const CustomizationsList = () => (
    <div className={styles.customizations}>
        {CUSTOMIZATIONS.map((customization) => {
            const Icon = customization.icon;
            return (
                <button
                    key={customization.key}
                    type="button"
                    className={styles.customizationCard}
                    onClick={() => openCustomization(customization)}
                >
                    <Icon className={styles.customizationIcon} />
                    <span className={styles.customizationText}>
                        <span className={styles.customizationTitle}>{customization.title}</span>
                        <span className={styles.customizationDescription}>{customization.description}</span>
                    </span>
                    <RiArrowRightSLine className={styles.customizationArrow} />
                </button>
            );
        })}
    </div>
);

const openPersonalInformationForm = (data, onSaved) => {
    const columns = EMPLOYEE_ADD_CONF.columns
        .filter((column) => SELF_EDITABLE_FIELDS.includes(column.name))
        .map((column) => ({ ...column, defaultValue: () => data[column.name] ?? '' }));
    addPopup({
        content: Form,
        popupData: {
            configuration: {
                name: 'myAccountPersonalInformation',
                formTitle: 'Edit Personal Information',
                customMethod: 'PATCH',
                addLink: API_ROOT + 'employees/me',
                customSubmitButton: 'Save Changes',
                successMessage: 'Personal information updated',
                columns,
            },
        },
        postSuccessAction: onSaved,
        EZCloseAllowed: false,
    });
};

const buildPageObject = (data, onProfileSaved) => ({
    header: {
        title: `${data.firstName} ${data.lastName}`,
        suplementaryInfo: data.roleName,
    },
    infoSections: [
        {
            title: 'Personal Information',
            editAccessLevel: true,
            editButton: {
                text: 'Edit',
                action: () => openPersonalInformationForm(data, onProfileSaved),
            },
            columns: [
                generateColumn(data, false, 'First Name', 'firstName'),
                generateColumn(data, false, 'Last Name', 'lastName'),
                generateColumn(data, false, 'Telegram', 'telegramUserName'),
                generateColumn(data, false, 'Phone', 'directLine'),
                generateColumn(data, false, 'Ext', 'extension'),
            ],
        },
        {
            title: 'Account',
            editAccessLevel: true,
            editButton: {
                text: 'Change Password',
                action: () => addPopup({
                    content: ChangePasswordPopup,
                    popupData: { login: data.login },
                    postSuccessAction: () => {},
                    EZCloseAllowed: false,
                }),
            },
            columns: [
                generateColumn(data, false, 'Username', 'login'),
                generateColumn(data, false, 'Email', 'email'),
            ],
        },
    ],
    overviewSection: [
        {
            title: 'Customizations',
            content: () => <CustomizationsList />,
        },
    ],
});

export const MyAccountPage = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        sendRequest('GET', API_ROOT + 'employees/me', controller.signal, (profile) => {
            storeCustomizations(profile.customizations);
            setData(profile);
        }, setError);
        return () => controller.abort();
    }, []);

    const onProfileSaved = (profile) => {
        if (!profile) return;
        localStorage.setItem('currentUserFirstName', profile.firstName);
        setData(profile);
    };

    if (error !== null) return <div className="errorMessage">Something went wrong</div>;
    if (data === null) return <LoadingIcon state={true} height={'10rem'} />;

    const pageObject = buildPageObject(data, onProfileSaved);
    return (
        <div className={itemPageStyles.wrapper} style={{ gridTemplateColumns: '22rem 1fr' }}>
            <InformationCard object={pageObject} setData={onProfileSaved} />
            <OverviewCard object={pageObject} setData={onProfileSaved} />
        </div>
    );
};
